import React, { useEffect } from 'react';
import { useStore } from '../../../../../stores/root';

// assets
import { faCheck, faTriangleExclamation, faXmarkCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Text from '../../../../typography/text';

// services
import ErrorService from '../../../../../services/general/error';
import ValidationService from '../../../../../services/validation';
import VendorsService from '../../../../../services/data/vendors';
import ShipsService from '../../../../../services/data/ships';

// props
import { IRoleResourceShortProps } from '../../../../../props/users/role';
import { IVendorResourceShortProps } from '../../../../../props/data/vendors';

// components
import { DefaultButton, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TagPicker, TextField } from '@fluentui/react';
import Label from '../../../../typography/label';
import PermissionsService from '../../../../../services/permissions';
import NoAccess from '../../../../uiframeworks/noAccess';
import { IShipResourceShortProps } from '../../../../../props/data/ships';
import VendorField from '../../../../uiframeworks/forms/vendor';
import SelectFleetType from '../../../../uiframeworks/forms/selectFleetType';
import { ISystemFleetTypeResourceShortProps } from '../../../../../props/systems/fleetTypes';

interface IShipFormProps {
    shipId?: string;
    vendorId?: string;
    onDismissed(refresh?: boolean, ship?: IShipResourceShortProps): void;
}

type FormDataProps = {
    name: string;
    registrationNumber: string;
    fleetType?: ISystemFleetTypeResourceShortProps;
    vendor?: IVendorResourceShortProps;
}

type FormDataErrorProps = {
    name?: string;
    registrationNumber?: string;
    vendor?: string;
    fleetType?: string;
}

const ShipForm: React.FC<IShipFormProps> = (props: IShipFormProps) => {
    const { banner, user } = useStore();
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [submitting, setSubmitting] = React.useState<boolean>(false);
    const [checking, setChecking] = React.useState<boolean>(false);
    const [similarShips, setSimilarShips] = React.useState<IShipResourceShortProps[]>([]);
    const [roles, setRoles] = React.useState<IRoleResourceShortProps[]>([]);
    const [data, setData] = React.useState<FormDataProps>({
        name: "",
        registrationNumber: ""
    });
    const [error, setError] = React.useState<FormDataErrorProps>({});
    const mode: 'create' | 'update' = props.shipId === undefined ? 'create' : 'update';

    let hasPermission = false;
    if (mode === 'create' && PermissionsService.hasPermission(['ships.create'], user.permissions)) {
        hasPermission = true;
    } else if (mode === 'update' && PermissionsService.hasPermission(['ships.update.all'], user.permissions)) {
        hasPermission = true;
    }

    useEffect(() => {
        init();
    }, []);

    const init = async () => {
        let _data = data;

        if (props.shipId) {
            const _ship = await ShipsService.get(props.shipId);
            _data = {
                name: _ship.name,
                registrationNumber: _ship.registrationNumber,
                vendor: _ship.vendor,
                fleetType: _ship.fleetType
            };
        } else if (props.vendorId) {
            const _vendor = await VendorsService.get(props.vendorId);
            _data.vendor = _vendor;
        }

        setData({..._data});
        setLoaded(true);
    }

    const isSubmitButtonDisabled = (): boolean => {
        if (error.name || error.vendor || error.registrationNumber || error.fleetType) {
            return true;
        } else if (data.name === '' || !data.vendor || !data.fleetType) {
            return true;
        }

        return false;
    }

    const _onSubmit = async () => {
        try {
            setSubmitting(true);
            const { name, registrationNumber, vendor, fleetType } = data;

            // create form data
            let fd = new FormData();
            fd.append("name", name);
            fd.append("registrationNumber", registrationNumber);
            if (vendor) {
                fd.append("vendorId", vendor.id);
            }
            if (fleetType) {
                fd.append("fleetTypeId", fleetType.id);
            }

            let result;
            if (props.shipId === undefined) {
                result = await ShipsService.new(fd);
            } else {
                result = await ShipsService.update(props.shipId, fd);
            }
            banner.add({
                key: mode + '_ship_success',
                variant: 'success',
                icon: faCheck,
                text: `Ship "${name}" ${mode}ed successfully`
            });
            props.onDismissed(true, result);
        } catch (e) {
            setSubmitting(false);
        }
    }

    return <Panel headerText={mode === 'create' ? 'Create Ship' : 'Update Ship'}
        isOpen={true}
        type={PanelType.medium}
        onDismiss={() => props.onDismissed(false)}
        isFooterAtBottom={true}
        onRenderFooterContent={() => {
            return <Stack horizontal tokens={{ childrenGap: 10 }}>
                {
                    !submitting && hasPermission ? (
                        <>
                            <PrimaryButton text={"Submit"} disabled={isSubmitButtonDisabled()} onClick={_onSubmit} />
                            <DefaultButton text={"Cancel"} onClick={() => { props.onDismissed(false) }} />
                        </>
                    ) : null
                }
                {submitting ? <Spinner size={SpinnerSize.medium} labelPosition={"right"} label={mode === 'create' ? "Creating ship ..." : "Updating ship ..."} /> : null}
            </Stack>;
        }}>
        <Stack tokens={{ childrenGap: 15 }} styles={{ root: { marginTop: 20 } }}>
            {!loaded ? <Stack horizontalAlign={"baseline"}><Spinner size={SpinnerSize.medium} labelPosition={"right"} label={"Preparing form ..."} /></Stack> : null}
            {
                loaded && hasPermission ? <>
                    <VendorField label={'Select vendor'}
                        selected={data.vendor}
                        disabled={submitting || props.vendorId !== undefined}
                        onChange={async (item) => {
                            data.vendor = item;
                            setData({ ...data });

                            const name = (data.name || "").trim();
                            if (name !== "" && mode === 'create') {
                                setChecking(true);
                                setSimilarShips([]);
                                try {
                                    const vendorQuery = item ? `&vendor_id=${item.id}` : '';
                                    const _similar = await ShipsService.findSimilar(`name=${encodeURIComponent(name)}${vendorQuery}`);
                                    setSimilarShips(_similar || []);
                                } catch (e) {}
                                setChecking(false);
                            } else {
                                setSimilarShips([]);
                            }
                        }} />
                    <Stack.Item>
                        <TextField label={"Name"}
                            required={true}
                            value={data.name}
                            onChange={(evt, value) => {
                                data.name = value || "";

                                const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 100 });
                                error.name = validation.message;

                                setData({ ...data });
                                setError({ ...error });
                            }}
                            onBlur={async () => {
                                const name = (data.name || "").trim();
                                if (name !== "" && mode === 'create') {
                                    setChecking(true);
                                    setSimilarShips([]);
                                    try {
                                        const vendorQuery = data.vendor ? `&vendor_id=${data.vendor.id}` : '';
                                        const _similar = await ShipsService.findSimilar(`name=${encodeURIComponent(name)}${vendorQuery}`);
                                        setSimilarShips(_similar || []);
                                    } catch (e) {}
                                    setChecking(false);
                                }
                            }}
                            errorMessage={error.name}
                            disabled={submitting} />
                        {checking ? (
                            <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center" styles={{ root: { marginTop: 6 } }}>
                                <Spinner size={SpinnerSize.xSmall} />
                                <Text size={'xsmall'} style={{ fontStyle: 'italic', color: '#605e5c' }}>Mengecek kemiripan nama kapal...</Text>
                            </Stack>
                        ) : null}
                        {similarShips.length > 0 ? (
                            <Stack tokens={{ childrenGap: 8 }} styles={{ root: { backgroundColor: '#fff4ce', border: '1px solid #ffd335', borderRadius: 4, padding: 12, marginTop: 8 } }}>
                                <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
                                    <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: '#795548', fontSize: 16 }} />
                                    <Text size={'small'} style={{ fontWeight: 600, color: '#323130' }}>
                                        Ditemukan {similarShips.length} kapal serupa {data.vendor ? `dari vendor ${data.vendor.name}` : 'yang sudah terdaftar di sistem'}:
                                    </Text>
                                </Stack>
                                <Text size={'xsmall'} style={{ color: '#605e5c' }}>
                                    Pastikan Anda tidak membuat kapal duplikat. Jika kapal yang dimaksud ada di bawah ini, silakan klik tombol <b>"Gunakan Kapal Ini"</b>:
                                </Text>
                                <Stack tokens={{ childrenGap: 8 }} styles={{ root: { marginTop: 4 } }}>
                                    {similarShips.map((ship) => (
                                        <Stack key={ship.id} className={'card'} horizontal horizontalAlign={'space-between'} verticalAlign={'center'} styles={{ root: { padding: '8px 12px', backgroundColor: '#ffffff', borderRadius: 4, border: '1px solid #edebe9' } }}>
                                            <Stack tokens={{ childrenGap: 2 }}>
                                                <Label size={'small'} style={{ fontWeight: 600 }}>{ship.name}</Label>
                                                <Stack horizontal tokens={{ childrenGap: 10 }}>
                                                    {ship.vendor ? <Text size={'xsmall'} style={{ color: '#0078d4' }}>Vendor: {ship.vendor.name}</Text> : null}
                                                    {ship.registrationNumber ? <Text size={'xsmall'} style={{ color: '#605e5c' }}>Reg: {ship.registrationNumber}</Text> : null}
                                                    {ship.fleetType ? <Text size={'xsmall'} style={{ color: '#605e5c' }}>Tipe: {ship.fleetType.name}</Text> : null}
                                                </Stack>
                                            </Stack>
                                            <PrimaryButton
                                                text={"Gunakan Kapal Ini"}
                                                styles={{ root: { fontSize: 12, height: 30, padding: '0 10px' } }}
                                                onClick={() => {
                                                    props.onDismissed(true, ship);
                                                }}
                                            />
                                        </Stack>
                                    ))}
                                </Stack>
                            </Stack>
                        ) : null}
                    </Stack.Item>
                    <Stack.Item>
                        <TextField label={"Registration Number"}
                            required={true}
                            value={data.registrationNumber}
                            onChange={(evt, value) => {
                                data.registrationNumber = value || "";

                                const validation = ValidationService.combination(value, ['required', 'limit'], { maxChars: 100 });
                                error.registrationNumber = validation.message;

                                setData({ ...data });
                                setError({ ...error });
                            }}
                            errorMessage={error.registrationNumber}
                            disabled={submitting} />
                    </Stack.Item>
                    <SelectFleetType selected={data.fleetType}
                        disabled={submitting}
                        errorMessage={error.fleetType}
                        qs={[`type=Ship`]}
                        required
                        onChange={(item) => {
                            data.fleetType = item;

                            const validation = ValidationService.combination(item, ['required'], {});
                            error.fleetType = validation.message;

                            setData({ ...data });
                        }} />
                </> : null
            }
            {!hasPermission ? <NoAccess /> : null}
        </Stack>
    </Panel>
};

export default ShipForm;
